import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faMapMarkerAlt, faEnvelope, faClock, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import '../@style/ContactPage.css';
import wpIcon from '../assets/icons/wp.png';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => {
      // Make hero elements visible immediately
      const heroElements = document.querySelectorAll('.contact-hero .fade-in-up');
      heroElements.forEach(element => {
        element.classList.add('initial');
      });

      const observerOptions = {
        threshold: 0.05,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          } else {
            // Element viewport'tan çıktığında animasyon sınıfını kaldır
            entry.target.classList.remove('animate');
          }
        });
      }, observerOptions);

      // Observe all animated elements except hero elements
      const animatedElements = document.querySelectorAll('.fade-in-up:not(.contact-hero .fade-in-up), .fade-in-left, .fade-in-right, .scale-in');
      
      animatedElements.forEach(element => {
        observer.observe(element);
      });

      return () => {
        animatedElements.forEach(element => {
          observer.unobserve(element);
        });
      };
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simüle edilmiş form gönderimi
    setTimeout(() => {
      alert(t('contact.form.success'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className={`contact-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="fade-in-up">{t('contact.title')}</h1>
          <p className="fade-in-up">{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="contact-info">
        <div className="container">
          <div className="info-grid">
            <div className="info-card fade-in-up">
              <div className="info-icon">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              <h3>{t('contact.phone.title')}</h3>
              <p>{t('contact.phone.number')}</p>
              <p>{t('contact.phone.hours')}</p>
            </div>

            <div className="info-card fade-in-up">
              <div className="info-icon">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <h3>{t('contact.address.title')}</h3>
              <div className="address-item">
                <strong>{t('contact.address.main')}</strong>
                <p>{t('contact.address.main_text').split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < t('contact.address.main_text').split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}</p>
              </div>
              <div className="address-item">
                <strong>{t('contact.address.branch')}</strong>
                <p>{t('contact.address.branch_text').split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < t('contact.address.branch_text').split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}</p>
              </div>
            </div>

            <div className="info-card fade-in-up">
              <div className="info-icon">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h3>{t('contact.email.title')}</h3>
              <p>{t('contact.email.address')}</p>
              <p>{t('contact.email.hours')}</p>
            </div>

            <div className="info-card fade-in-up">
              <div className="info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <h3>{t('contact.hours.title')}</h3>
              <p>{t('contact.hours.weekdays')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="form-content">
            <div className="form-info fade-in-left">
              <h2>{t('contact.form.title')}</h2>
              <p>
                {t('contact.form.description')}
              </p>
              
              <div className="contact-features">
                <div className="feature-item">
                  <div className="feature-icon">📞</div>
                  <div>
                    <h4>{t('contact.form.feature1.title')}</h4>
                    <p>{t('contact.form.feature1.content')}</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon">💬</div>
                  <div>
                    <h4>{t('contact.form.feature2.title')}</h4>
                    <p>{t('contact.form.feature2.content')}</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon">🎯</div>
                  <div>
                    <h4>{t('contact.form.feature3.title')}</h4>
                    <p>{t('contact.form.feature3.content')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form fade-in-right">
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">{t('contact.form.name')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder={t('contact.form.name_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">{t('contact.form.email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder={t('contact.form.email_placeholder')}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">{t('contact.form.phone')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('contact.form.phone_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="subject">{t('contact.form.subject')}</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">{t('contact.form.subject_placeholder')}</option>
                      <option value="kiymetli-maden-alis">{t('contact.form.subject1')}</option>
                      <option value="kiymetli-maden-satis">{t('contact.form.subject2')}</option>
                      <option value="toptan-satis">{t('contact.form.subject4')}</option>
                      <option value="genel-bilgi">{t('contact.form.subject5')}</option>
                      <option value="diger">{t('contact.form.subject6')}</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contact.form.message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="5"
                    placeholder={t('contact.form.message_placeholder')}
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="fade-in-up">{t('contact.locations.title')}</h2>
          <div className="maps-grid">
            <div className="map-container fade-in-up">
              <h3>{t('contact.locations.main')}</h3>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.0912764423515!2d29.012979976554153!3d41.066997415806405!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab65067842823%3A0x8d8c665ab915f7de!2sZorlu%20Center!5e0!3m2!1str!2str!4v1764945153601!5m2!1str!2str"
                width="100%" 
                height="400" 
                style={{border:0}} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title={t('contact.locations.main_title')}
                className="contact-map"
              ></iframe>
            </div>
            <div className="map-container fade-in-up">
              <h3>{t('contact.locations.branch')}</h3>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3010.727518244813!2d28.9705481!3d41.00933680000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab993e209a207%3A0x962770072e8b477f!2sMasgold!5e0!3m2!1str!2str!4v1765889051917!5m2!1str!2str" 
                width="100%" 
                height="400" 
                style={{border:0}} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title={t('contact.locations.branch_title')}
                className="contact-map"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Contact */}
      <section className="whatsapp-contact">
        <div className="container">
          <div className="whatsapp-content">
            <div className="whatsapp-icon scale-in">
              <img src={wpIcon} alt={t('contact.whatsapp.alt')} />
            </div>
            <div className="whatsapp-text fade-in-left">
              <h3>{t('contact.whatsapp.title')}</h3>
              <p>{t('contact.whatsapp.description')}</p>
            </div>
            <a href="https://wa.me/905010020000" className="whatsapp-btn scale-in" target="_blank" rel="noopener noreferrer">
              {t('contact.whatsapp.button')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
